import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { addJobSchedule } from '../services/scheduleService';
import { searchFranchisees } from '../services/franchiseeService';
import { searchClients as searchSuppliers, getClientById } from '../services/clientService';
import { searchHouseholds as searchAppClients, getClientByHouseholdId } from '../services/householdService';
import { listServiceTypes } from '../services/serviceTypeService';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { FaMapMarkerAlt, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { terminology, allocationTypes, scheduleTypes, frequencyOptions, frequencyIntervalUnits, quoteStatuses, allocationOptions, allocationIntervalUnits } from '../config/terminology';
import ConfirmationModal from '../components/ConfirmationModal';
import { Button } from '../components/forms';
import { isCarwashService } from '../utils/carwashUtils';
import { InputField, SelectField, TextareaField, ReadOnlyField } from '../components/fields';

const useSearch = (searchFunction, options = {}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false); // Flag to prevent blur during selection

    const optionsString = JSON.stringify(options);

    const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
        if (!searchFunction || term.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsSearching(true);
        try {
            const data = await searchFunction({ searchString: term, ...fetchOptions });
            let results = data.franchisees || data.clients || data.households || [];
            
            // Filter out inactive households if this is a household search
            if (data.households) {
                results = results.filter(household => household.status === 'active');
            }
            
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearching(false);
        }
    }, 300), [searchFunction]);

    useEffect(() => {
        const currentOptions = JSON.parse(optionsString);
        debouncedFetch(searchTerm, currentOptions);
        return () => debouncedFetch.cancel();
    }, [searchTerm, optionsString, debouncedFetch]);

    return {
        searchTerm, setSearchTerm,
        suggestions, setSuggestions,
        isSearching,
        showSuggestions, setShowSuggestions,
        isSelecting, setIsSelecting
    };
};

const AddJobSchedulePage = () => {
    const { register, handleSubmit, watch, setValue, resetField, formState: { errors, isSubmitting, isSubmitted } } = useForm({
        mode: 'onBlur',        defaultValues: {
            fseId: '',
            fseNameSearch: '',
            clientId: '',
            clientNameSearch: '',
            householdId: '',
            householdNameSearch: '',
            scheduleType: '',
            startDate: '',
            endDate: '',
            occurrences: '',
            jobTime: '',
            jobDate: '',
            frequencyOption: '',
            frequencyIntervalValue: '',
            frequencyIntervalUnit: 'Days',
            allotedHours: '',
            pricePerHour: '',
            additionalPrice: '',
            allocationType: 'fixed-fee',
            allocationOption: 'interval',
            allocationIntervalValue: 7,
            allocationIntervalUnit: 'Days',
            invoicePricePerHour: '',
            description: '',
            adminNotes: '',
            notesForFranchisee: '',
            quoteStatus: 'approved',
            ndisJob: false,
        }
    });

    const navigate = useNavigate();
    const location = useLocation();
    const [apiError, setApiError] = useState(null);
    const [serviceSelectionError, setServiceSelectionError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [formDataForConfirmation, setFormDataForConfirmation] = useState(null);
    const [selectedAppClient, setSelectedAppClient] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);

    const franchiseeSearch = useSearch(searchFranchisees);
    const supplierSearchOptions = useMemo(() => ({}), []);
    const supplierSearch = useSearch(searchSuppliers, supplierSearchOptions);
    const selectedSupplierId = watch('clientId');
    // Pass clientId directly as a primitive, not as an object
    const appClientSearch = useSearch(searchAppClients, { clientId: selectedSupplierId });
    const scheduleType = watch('scheduleType');
    const frequencyOption = watch('frequencyOption');
    const frequencyIntervalValue = watch('frequencyIntervalValue');
    const frequencyIntervalUnit = watch('frequencyIntervalUnit');
    const additionalPrice = watch('additionalPrice');
    const allocationType = watch('allocationType');
    const allocationOption = watch('allocationOption');
    const quoteStatus = watch('quoteStatus');
    const selectedAppClientId = watch('householdId');
    const ndisJob = watch('ndisJob');
    const invoicePricePerHour = watch('invoicePricePerHour');

    useEffect(() => {
        const fetchServiceTypes = async () => {
            try {
                const services = await listServiceTypes();
                setAllServiceTypes(services);
            } catch (error) {
                console.error("Failed to fetch service types:", error);
                setApiError("Could not load service types.");
            } finally {
                setServiceTypesLoading(false);
            }
        };
        fetchServiceTypes();
    }, []);

    // Fetch supplier data when supplier is selected
    useEffect(() => {
        const fetchSupplierData = async () => {
            if (selectedSupplierId) {
                try {
                    const supplierData = await getClientById(selectedSupplierId);
                    setSelectedSupplier(supplierData);
                } catch (error) {
                    console.error("Failed to fetch supplier data:", error);
                    setSelectedSupplier(null);
                }
            } else {
                setSelectedSupplier(null);
            }
        };
        fetchSupplierData();
    }, [selectedSupplierId]);

    // Auto-update description when invoice price changes and supplier has hourly rate (aged care only)
    useEffect(() => {
        if (invoicePricePerHour && selectedSupplier?.agedCare && selectedSupplier?.hourlyRate) {
            const price = parseFloat(invoicePricePerHour);
            const hourlyRate = parseFloat(selectedSupplier.hourlyRate);

            if (!isNaN(price) && !isNaN(hourlyRate) && hourlyRate > 0) {
                // Calculate price ex GST based on NDIS job flag
                const priceExGST = ndisJob ? price : price / 1.1;
                let duration = priceExGST / hourlyRate;

                // Round up to 15-minute increments (0.25 hours) if durationInIncrements is enabled
                if (selectedSupplier?.durationInIncrements) {
                    duration = Math.ceil(duration * 4) / 4;
                }

                const descriptionText = `Duration ${duration.toFixed(2)} hours based on hourly rate of $${hourlyRate.toFixed(2)} + GST.`;
                setValue('description', descriptionText, { shouldValidate: false });
            }
        }
    }, [invoicePricePerHour, selectedSupplier, ndisJob, setValue]);

    useEffect(() => {
        if (additionalPrice && !isNaN(parseFloat(additionalPrice)) && parseFloat(additionalPrice) >= 0) {
            const invoicePriceExGST = parseFloat(additionalPrice);
            // Don't add GST for NDIS jobs
            const invoicePriceWithGST = ndisJob
                ? invoicePriceExGST.toFixed(2)
                : (invoicePriceExGST * 1.1).toFixed(2);
            setValue('invoicePricePerHour', invoicePriceWithGST, { shouldValidate: true });
        } else {
            setValue('invoicePricePerHour', '', { shouldValidate: false });
        }
    }, [additionalPrice, ndisJob, setValue]);

    // Sync allocation fields with frequency fields for Fixed Hours allocation type (only for recurring schedules)
    useEffect(() => {
        if (allocationType === 'fixed-hours' && scheduleType === 'recurring') {
            setValue('allocationOption', frequencyOption, { shouldValidate: false });
            setValue('allocationIntervalValue', frequencyIntervalValue, { shouldValidate: false });
            setValue('allocationIntervalUnit', frequencyIntervalUnit, { shouldValidate: false });
        }
    }, [allocationType, scheduleType, frequencyOption, frequencyIntervalValue, frequencyIntervalUnit, setValue]);

    // Reset description when allocation type changes
    useEffect(() => {
        setValue('description', '', { shouldValidate: false });
    }, [allocationType, setValue]);

    const createSearchHandlers = (fieldNamePrefix, searchState, idField, nameField) => ({
        onChange: (e) => {
            const value = e.target.value;
            searchState.setSearchTerm(value);
            setValue(fieldNamePrefix + 'NameSearch', value);
            setValue(idField, '');
            if (fieldNamePrefix === 'client') {
                setValue('householdId', '');
                setValue('householdNameSearch', '');
                setSelectedAppClient(null);
                appClientSearch.setSearchTerm('');
                appClientSearch.setSuggestions([]);
            } else if (fieldNamePrefix === 'household') {
                setSelectedAppClient(null);
            }
            if (value.length < 3) searchState.setShowSuggestions(false);
        },
        onSelect: async (item) => {
            searchState.setIsSelecting(true); // Set flag to prevent blur
            searchState.setShowSuggestions(false);
            searchState.setSuggestions([]);

            setValue(idField, item.id);
            let displayName;

            if (fieldNamePrefix === 'household') {
                displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                setSelectedAppClient(item);

                // If no supplier is selected, automatically fetch and populate client info
                if (!watch('clientId')) {
                    try {
                        const clientData = await getClientByHouseholdId(item.id);
                        if (clientData) {
                            setValue('clientId', clientData.id);
                            setValue('clientNameSearch', clientData.clientName || '');
                        }
                    } catch (error) {
                        console.error('Failed to fetch client for household:', error);
                    }
                }
                // Clear search term and suggestions to prevent dropdown from reappearing
                searchState.setSearchTerm('');
                searchState.setSuggestions([]);
            } else if (fieldNamePrefix === 'fse') {
                const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            } else {
                displayName = item.clientName || '';
                setSelectedAppClient(null);
            }

            setValue(fieldNamePrefix + 'NameSearch', displayName);

            // Reset the flag after a brief delay
            setTimeout(() => {
                searchState.setIsSelecting(false);
            }, 100);
        },
        renderSuggestions: (item) => {
            if (fieldNamePrefix === 'fse') {
                const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            } else if (fieldNamePrefix === 'household') {
                return `${item.firstName || ''} ${item.lastName || ''}`.trim() + (item.addressLine1 ? ` (${item.addressLine1})` : '');
            } else {
                return item.clientName || '';
            }
        }
    });

    const fseHandlers = createSearchHandlers('fse', franchiseeSearch, 'fseId', 'franchiseName');
    const supplierHandlers = createSearchHandlers('client', supplierSearch, 'clientId', 'clientName');
    const appClientHandlers = createSearchHandlers('household', appClientSearch, 'householdId', 'householdName');

    const handleServiceToggle = (serviceId) => {
        setSelectedServiceIds(prev => {
            const isCurrentlySelected = prev.includes(serviceId);
            const isCarwash = isCarwashService(serviceId, allServiceTypes);

            // If deselecting the service
            if (isCurrentlySelected) {
                return prev.filter(id => id !== serviceId);
            }

            // If selecting a carwash service
            if (isCarwash) {
                // Clear all other selections and only select carwash
                return [serviceId];
            }

            // If selecting a non-carwash service
            // Check if any currently selected services are carwash
            const hasCarwashSelected = prev.some(id => isCarwashService(id, allServiceTypes));

            if (hasCarwashSelected) {
                // Replace carwash selection with this new service
                return [serviceId];
            } else {
                // Normal multi-selection for non-carwash services
                return [...prev, serviceId];
            }
        });
        setServiceSelectionError(null);
    };

    const onSubmit = async (data) => {
        setApiError(null);
        setServiceSelectionError(null);

        // 🔍 LOG: Form field values when "Schedule Job" button is pressed
        console.log('📝 FORM SUBMISSION - Raw form data:', {
            allocationType: data.allocationType,
            allocationOption: data.allocationOption,
            allocationIntervalValue: data.allocationIntervalValue,
            allocationIntervalUnit: data.allocationIntervalUnit,
            frequencyOption: data.frequencyOption,
            frequencyIntervalValue: data.frequencyIntervalValue,
            frequencyIntervalUnit: data.frequencyIntervalUnit,
            scheduleType: data.scheduleType,
            allFormData: data
        });

        if (selectedServiceIds.length === 0) {
            setServiceSelectionError("Please select at least one service.");
            return;
        }

        const serviceNamesMap = new Map(allServiceTypes.map(service => [service.id, service.name]));
        const servicesArray = selectedServiceIds.map(id => ({ serviceTypeId: id, name: serviceNamesMap.get(id) })).filter(s => s.name);

        console.log('Constructed servicesArray:', servicesArray); // Log servicesArray immediately after creation

        if (servicesArray.length !== selectedServiceIds.length) {
             setServiceSelectionError("Could not find names for all selected services. Please try again.");
             console.error("Failed to map service IDs to names:", selectedServiceIds, serviceNamesMap);
             return;
        }        let submitData = {
            ...data,
            services: servicesArray, // Array of { serviceTypeId, name }
            allotedHours: data.allotedHours ? parseFloat(data.allotedHours) : null,
            pricePerHour: data.pricePerHour ? parseFloat(data.pricePerHour) : null,
            additionalPrice: data.additionalPrice ? parseFloat(data.additionalPrice) : null,
            invoicePricePerHour: data.invoicePricePerHour ? parseFloat(data.invoicePricePerHour) : null,
            jobTime: data.jobTime || null,
            description: data.description || null,
            adminNotes: data.adminNotes || null,
            notesForFranchisee: data.notesForFranchisee || null,
            quoteStatus: data.quoteStatus || 'approved', // Pass quoteStatus from form, default to 'approved'
            ndisJob: data.ndisJob || false, // Include NDIS Job flag
        };

        // DEBUG: Log NDIS job field tracking
        console.log('🔍 ADD JOB SCHEDULE - NDIS DEBUGGING:');
        console.log('  Form data.ndisJob:', data.ndisJob);
        console.log('  Submit data.ndisJob:', submitData.ndisJob);
        console.log('  Full submitData before cleanup:', JSON.stringify(submitData, null, 2));

        if (submitData.allotedHours === null) delete submitData.allotedHours;
        if (submitData.pricePerHour === null) delete submitData.pricePerHour;
        if (submitData.additionalPrice === null) delete submitData.additionalPrice;
        if (submitData.invoicePricePerHour === null) delete submitData.invoicePricePerHour;
        if (submitData.jobTime === null) delete submitData.jobTime;
        if (submitData.description === null) delete submitData.description;
        if (submitData.adminNotes === null) delete submitData.adminNotes;
        if (submitData.notesForFranchisee === null) delete submitData.notesForFranchisee;

        if (submitData.scheduleType === 'one-off') {
            submitData.jobDate = submitData.startDate;
        } else {
            delete submitData.jobDate;
        }

        if (submitData.scheduleType === 'recurring') {
            if (submitData.frequencyOption === 'monthly') {
                submitData.frequencyType = 'Monthly';
                delete submitData.frequencyValue;
            } else if (submitData.frequencyOption === 'interval') {
                submitData.frequencyType = submitData.frequencyIntervalUnit;
                submitData.frequencyValue = parseInt(submitData.frequencyIntervalValue, 10);
            } else {
                // No frequency selected - don't send frequency fields at all
                delete submitData.frequencyType;
                delete submitData.frequencyValue;
            }
        }

        // Transform allocation fields for backend
        if (submitData.allocationType === 'fixed-fee') {
            // Keep as 'fixed-fee', no allocationValue needed
            delete submitData.allocationValue;
        } else if (submitData.allocationType === 'fixed-hours') {
            if (submitData.allocationOption === 'monthly') {
                submitData.allocationType = 'monthly';
                delete submitData.allocationValue;
            } else {
                submitData.allocationType = submitData.allocationIntervalUnit.toLowerCase(); // 'days' or 'weeks'
                submitData.allocationValue = parseInt(submitData.allocationIntervalValue, 10);
            }
        }

        delete submitData.frequencyOption;
        delete submitData.frequencyIntervalValue;
        delete submitData.frequencyIntervalUnit;
        
        // Delete allocation UI-specific fields after transformation
        delete submitData.allocationOption;
        delete submitData.allocationIntervalValue;
        delete submitData.allocationIntervalUnit;        delete submitData.fseNameSearch;
        delete submitData.clientNameSearch;
        delete submitData.householdNameSearch;

        // DEBUG: Final data being sent to API
        console.log('  Final submitData sent to API:', JSON.stringify(submitData, null, 2));

        try {
            await addJobSchedule(submitData);
            
            // Preserve filters when navigating back after successful add
            const navigationState = { message: 'Job schedule added successfully!' };
            if (location.state?.filters) {
                navigationState.filters = location.state.filters;
            }
            
            navigate('/job-scheduler', { state: navigationState });
        } catch (error) {
            setApiError(error.message || 'Failed to add job schedule. Please check details.');
            console.error("Submission error:", error);
        }
    };

    const formatAddress = (appClient) => {
        if (!appClient) return '';
        const parts = [
            appClient.addressLine1,
            appClient.addressLine2,
            appClient.state,
            appClient.postalCode,
        ];
        return parts.filter(Boolean).join(', ');
    };

    // Helper to build a formatted summary for the modal
    const buildJobSummary = (data) => {
        const isQuoteRequest = data.quoteStatus === 'pending-quote';
        
        // Helper to check if a value is a valid number
        const isValidNumber = (value) => {
            return value !== '' && value !== null && value !== undefined && !isNaN(parseFloat(value)) && isFinite(value);
        };
        
        // Helper to format frequency
        const getFrequencyText = (data) => {
            if (data.scheduleType !== 'recurring') return null;
            if (data.frequencyOption === 'monthly') return 'Monthly';
            if (data.frequencyOption === 'interval' && data.frequencyIntervalValue && data.frequencyIntervalUnit) {
                return `Every ${data.frequencyIntervalValue} ${data.frequencyIntervalUnit.toLowerCase()}`;
            }
            return null;
        };
        
        return (
            <div className="space-y-4 text-left">
                {/* Basic Information */}
                <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Basic Information</h4>
                    <div className="space-y-1 text-sm">
                        {data.fseNameSearch && <div><span className="font-medium">Franchisee:</span> {data.fseNameSearch}</div>}
                        {data.clientNameSearch && <div><span className="font-medium">{terminology.client.singular}:</span> {data.clientNameSearch}</div>}
                        {data.householdNameSearch && <div><span className="font-medium">{terminology.household.singular}:</span> {data.householdNameSearch}</div>}
                    </div>
                </div>

                {/* Schedule Details */}
                {(data.scheduleType || data.startDate || data.endDate || data.jobTime || getFrequencyText(data)) && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Schedule Details</h4>
                        <div className="space-y-1 text-sm">
                            {data.scheduleType && <div><span className="font-medium">Schedule Type:</span> {data.scheduleType}</div>}
                            {getFrequencyText(data) && <div><span className="font-medium">Frequency:</span> {getFrequencyText(data)}</div>}
                            {data.startDate && <div><span className="font-medium">{data.scheduleType === 'one-off' ? 'Date' : 'Start Date'}:</span> {data.startDate}</div>}
                            {data.endDate && <div><span className="font-medium">End Date:</span> {data.endDate}</div>}
                            {data.occurrences && isValidNumber(data.occurrences) && <div><span className="font-medium">Occurrences:</span> {data.occurrences}</div>}
                            {data.jobTime && <div><span className="font-medium">Job Time:</span> {data.jobTime}</div>}
                        </div>
                    </div>
                )}

                {/* Services */}
                {selectedServiceIds.length > 0 && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Selected Services</h4>
                        <div className="flex flex-wrap gap-1 text-sm">
                            {selectedServiceIds.map(serviceId => {
                                const service = allServiceTypes?.find(s => s.id === serviceId);
                                return service ? (
                                    <span key={serviceId} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                        {service.name}
                                    </span>
                                ) : null;
                            })}
                        </div>
                    </div>
                )}

                {/* Pricing & Allocation */}
                {!isQuoteRequest && (data.allocationType || isValidNumber(data.allotedHours) || isValidNumber(data.pricePerHour) || isValidNumber(data.additionalPrice) || isValidNumber(data.invoicePricePerHour)) && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Pricing & Allocation</h4>
                        <div className="space-y-1 text-sm">
                            {data.allocationType && <div><span className="font-medium">Allocation Type:</span> {data.allocationType}</div>}
                            {isValidNumber(data.allotedHours) && <div><span className="font-medium">Allotted Hours:</span> {data.allotedHours}</div>}
                            {isValidNumber(data.pricePerHour) && <div><span className="font-medium">FSE Price:</span> ${parseFloat(data.pricePerHour).toFixed(2)}</div>}
                            {isValidNumber(data.additionalPrice) && <div><span className="font-medium">Invoice Price (ex. GST):</span> ${parseFloat(data.additionalPrice).toFixed(2)}</div>}
                            {isValidNumber(data.invoicePricePerHour) && <div><span className="font-medium">Invoice Price:</span> ${parseFloat(data.invoicePricePerHour).toFixed(2)}</div>}
                        </div>
                    </div>
                )}

                {/* Notes */}
                {(data.description || data.adminNotes || data.notesForFranchisee) && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Additional Notes</h4>
                        <div className="space-y-2 text-sm">
                            {data.description && (
                                <div>
                                    <span className="font-medium">Description (Invoice notes):</span>
                                    <div className="bg-gray-50 p-2 rounded-xl mt-1 text-gray-700">{data.description}</div>
                                </div>
                            )}
                            {data.adminNotes && (
                                <div>
                                    <span className="font-medium">Admin Notes:</span>
                                    <div className="bg-gray-50 p-2 rounded-xl mt-1 text-gray-700">{data.adminNotes}</div>
                                </div>
                            )}
                            {data.notesForFranchisee && (
                                <div>
                                    <span className="font-medium">Notes for Franchisee:</span>
                                    <div className="bg-gray-50 p-2 rounded-xl mt-1 text-gray-700">{data.notesForFranchisee}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Intercept submit to show modal first
    const handleFormSubmit = (data) => {
        setFormDataForConfirmation(data);
        setShowConfirmModal(true);
    };

    // Only submit after confirmation
    const handleConfirm = async () => {
        setShowConfirmModal(false);
        if (formDataForConfirmation) {
            await onSubmit(formDataForConfirmation);
            setFormDataForConfirmation(null);
        }
    };

    return (
        <div className="max-w-4xl mx-auto pt-8 pb-20">
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Schedule New Job(s)</h1>
                    <Link 
                        to="/job-scheduler" 
                        state={location.state?.filters ? { filters: location.state.filters } : {}}
                        className="text-gray-900 hover:text-green-600 underline"
                    >
                        Back to Scheduler
                    </Link>
                </div>

                {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded-xl mb-4">{apiError}</p>}

                {/* --- JOB APPROVAL RADIO BUTTONS (moved up) --- */}
                <div className="col-span-1 md:col-span-4 mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Approval</label>
                    <div className="flex items-center space-x-6">
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                value="approved"
                                {...register("quoteStatus", { required: "Please select job approval status." })}
                                defaultChecked
                                className="form-radio h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                            />
                            <span className="ml-2">This job is approved</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                value="pending-quote"
                                {...register("quoteStatus", { required: "Please select job approval status." })}
                                className="form-radio h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                            />
                            <span className="ml-2">This job requires quotation</span>
                        </label>
                    </div>
                    {errors.quoteStatus && <p className="mt-1 text-xs text-red-500">{errors.quoteStatus.message}</p>}
                </div>
                {/* --- END JOB APPROVAL RADIO BUTTONS --- */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                        <label htmlFor="fseNameSearch" className="block text-sm font-medium text-gray-700">Franchisee *</label>
                        <input id="fseNameSearch" type="text" autoComplete="off"
                            {...register("fseNameSearch", { required: "Franchisee search is required." })}
                            value={watch('fseNameSearch')}
                            onChange={fseHandlers.onChange}
                            onBlur={() => franchiseeSearch.setShowSuggestions(false)}
                            onFocus={() => {
                                if (franchiseeSearch.suggestions.length > 0 && !watch('fseId')) {
                                    franchiseeSearch.setShowSuggestions(true);
                                }
                            }}
                            className={`mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 ${errors.fseNameSearch || (errors.fseId && !watch('fseId')) ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <input type="hidden" {...register("fseId", { required: "Please select a franchisee." })} />
                        {errors.fseId && !watch('fseId') && <p className="mt-1 text-xs text-red-500">{errors.fseId.message}</p>}
                        {franchiseeSearch.showSuggestions && (
                            <ul className="suggestions-list">
                                {franchiseeSearch.isSearching ? <li className="searching">Searching...</li> :
                                 franchiseeSearch.suggestions.length > 0 ? franchiseeSearch.suggestions.map(item => (
                                    <li key={item.id} className="suggestion-item" 
                                        onMouseDown={() => {
                                            fseHandlers.onSelect(item);
                                            franchiseeSearch.setShowSuggestions(false);
                                            franchiseeSearch.setSearchTerm('');
                                        }}>
                                        {fseHandlers.renderSuggestions(item)}
                                    </li>
                                )) : franchiseeSearch.searchTerm.length >= 3 && <li className="no-results">No franchisees found.</li>}
                            </ul>
                        )}
                    </div>

                    <div className="relative">
                        <label htmlFor="clientNameSearch" className="block text-sm font-medium text-gray-700">{terminology.client.singular} *</label>
                        <input id="clientNameSearch" type="text" autoComplete="off"
                            {...register("clientNameSearch", { required: `${terminology.client.singular} search is required.` })}
                            value={watch('clientNameSearch')}
                            onChange={supplierHandlers.onChange}
                            onBlur={() => supplierSearch.setShowSuggestions(false)}
                            onFocus={() => {
                                if (supplierSearch.suggestions.length > 0 && !watch('clientId')) {
                                    supplierSearch.setShowSuggestions(true);
                                }
                            }}
                            className={`mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 ${errors.clientNameSearch || (errors.clientId && !watch('clientId')) ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <input type="hidden" {...register("clientId", { required: `Please select a ${terminology.client.singular.toLowerCase()}.` })} />
                        {errors.clientId && !watch('clientId') && <p className="mt-1 text-xs text-red-500">{errors.clientId.message}</p>}
                        {supplierSearch.showSuggestions && (
                             <ul className="suggestions-list">
                                {supplierSearch.isSearching ? <li className="searching">Searching...</li> :
                                 supplierSearch.suggestions.length > 0 ? supplierSearch.suggestions.map(item => (
                                    <li key={item.id} className="suggestion-item" 
                                        onMouseDown={() => {
                                            supplierHandlers.onSelect(item);
                                            supplierSearch.setShowSuggestions(false);
                                            supplierSearch.setSearchTerm('');
                                        }}>
                                        {supplierHandlers.renderSuggestions(item)}
                                    </li>
                                )) : supplierSearch.searchTerm.length >= 3 && <li className="no-results">No {terminology.client.plural.toLowerCase()} found.</li>}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="relative">
                    <label htmlFor="householdNameSearch" className="block text-sm mt-4 font-medium text-gray-700">{terminology.household.singular} *</label>
                    <input id="householdNameSearch" type="text" autoComplete="off"
                        {...register("householdNameSearch", { required: `${terminology.household.singular} search is required.` })}
                        onChange={appClientHandlers.onChange}
                        onBlur={() => appClientSearch.setShowSuggestions(false)}
                        onFocus={() => {
                            if (appClientSearch.suggestions.length > 0 && !watch('householdId')) {
                                appClientSearch.setShowSuggestions(true);
                            }
                        }}
                        className={`mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 ${errors.householdNameSearch || (errors.householdId && !watch('householdId')) ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Type to search..."
                    />
                    <input type="hidden" {...register("householdId", { required: `Please select a ${terminology.household.singular.toLowerCase()}.` })} />
                    {errors.householdId && !watch('householdId') && <p className="mt-1 text-xs text-red-500">{errors.householdId.message}</p>}
                    {appClientSearch.showSuggestions && appClientSearch.searchTerm && !appClientSearch.isSelecting && (
                         <ul className="suggestions-list">
                            {appClientSearch.isSearching ? <li className="searching">Searching...</li> :
                             appClientSearch.suggestions.length > 0 ? appClientSearch.suggestions.map(item => (
                                <li key={item.id} className="suggestion-item" 
                                    onMouseDown={() => {
                                        appClientHandlers.onSelect(item);
                                        appClientSearch.setShowSuggestions(false);
                                    }}>
                                    {`${item.firstName || ''} ${item.lastName || ''}`.trim()} ({item.addressLine1 || 'No Address'})
                                </li>
                            )) : appClientSearch.searchTerm.length >= 3 && !appClientSearch.isSearching && <li className="no-results">No {terminology.household.plural.toLowerCase()} found{selectedSupplierId ? ` for this ${terminology.client.singular.toLowerCase()}` : ''}.</li>}
                        </ul>
                    )}
                    {selectedAppClientId && selectedAppClient && (
                        <>
                            <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                                <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                                {formatAddress(selectedAppClient)}
                            </div>
                            {(selectedAppClient.siteContact?.name || selectedAppClient.siteContact?.email || selectedAppClient.siteContact?.phone) && (
                                <div className="mt-2 bg-gray-100 rounded-2xl px-4 py-3 flex flex-wrap gap-6 items-center">
                                    {selectedAppClient.siteContact?.name && (
                                        <div className="min-w-[160px] flex-1">
                                            <span className="block text-xs font-semibold text-gray-600">Case Manager</span>
                                            <span className="block text-sm text-gray-800">{selectedAppClient.siteContact.name}</span>
                                        </div>
                                    )}
                                    {selectedAppClient.siteContact?.email && (
                                        <div className="min-w-[160px] flex-1">
                                            <span className="block text-xs font-semibold text-gray-600">Email</span>
                                            <span className="block text-sm text-gray-800">{selectedAppClient.siteContact.email}</span>
                                        </div>
                                    )}
                                    {selectedAppClient.siteContact?.phone && (
                                        <div className="min-w-[160px] flex-1">
                                            <span className="block text-xs font-semibold text-gray-600">Phone</span>
                                            <span className="block text-sm text-gray-800">{selectedAppClient.siteContact.phone}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div>
                    <label className="block text-sm mt-4 font-medium text-gray-700 mb-2">Services *</label>
                    {serviceTypesLoading ? (
                        <p className="text-gray-500">Loading services...</p>
                    ) : allServiceTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {allServiceTypes.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                const isCarwash = isCarwashService(service.id, allServiceTypes);
                                const hasCarwashSelected = selectedServiceIds.some(id => isCarwashService(id, allServiceTypes));
                                const hasNonCarwashSelected = selectedServiceIds.some(id => !isCarwashService(id, allServiceTypes));

                                // Determine if this service should be disabled
                                const isDisabled = (isCarwash && hasNonCarwashSelected) || (!isCarwash && hasCarwashSelected);

                                return (
                                    <button
                                        key={service.id}
                                        type="button"
                                        onClick={() => !isDisabled && handleServiceToggle(service.id)}
                                        disabled={isDisabled}
                                        className={`
                                            px-3 py-1 rounded-full text-sm font-medium border transition-colors duration-150
                                            flex items-center justify-center
                                            ${isSelected
                                                ? 'bg-green-600 text-white border-green-600'
                                                : isDisabled
                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                                            }
                                        `}
                                        title={isDisabled
                                            ? (isCarwash
                                                ? 'Cannot select carwash services with other services'
                                                : 'Cannot select other services when carwash is selected')
                                            : ''
                                        }
                                    >
                                        {isSelected && <FaCheck className="mr-1 text-xs" />}
                                        {service.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-gray-500">No services available.</p>
                    )}
                    {serviceSelectionError && <p className="mt-2 text-xs text-red-500">{serviceSelectionError}</p>}
                </div>

                {/* --- Schedule Type/Start Date/Job Time section (with dynamic required) --- */}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end mt-4">
                     <div>
                        <SelectField
                            label={`Schedule Type${quoteStatus === 'pending-quote' ? '' : ' *'}`}
                            id="scheduleType"
                            register={register("scheduleType", { required: quoteStatus === 'pending-quote' ? false : "Schedule Type is required." })}
                            error={errors.scheduleType}
                        >
                            <option value="">Select Type...</option>
                            {scheduleTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </SelectField>
                    </div>
                     <div>
                        <InputField
                            label={scheduleType === 'one-off' ? 'Date' : 'Start Date'}
                            id="startDate" type="date"
                            register={register("startDate")}
                            error={errors.startDate}
                        />
                    </div>
                     <div>
                        <InputField label="Job Time (Optional)" id="jobTime" type="time"
                            register={register("jobTime")}
                            error={errors.jobTime}
                        />
                    </div>
                </div>

                {/* --- Recurring Details section (no heading, radio buttons side by side) --- */}
                {scheduleType === 'recurring' && (
                    <div className="mt-6">
                        {/* <h3 className="text-lg font-medium text-gray-800 mb-3">Job Frequency</h3> */}
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                Job Frequency (Optional)
                            </label>
                        <div className="flex items-center gap-8 mb-4">
                            <label className="flex items-center">
                                <input
                                    id="frequencyOptionInterval"
                                    type="radio"
                                    value="interval"
                                    {...register("frequencyOption")}
                                    className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                                />
                                <span className="ml-2">Every</span>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    {...register("frequencyIntervalValue", {
                                        required: frequencyOption === 'interval' ? "Value is required." : false,
                                        valueAsNumber: frequencyOption === 'interval' ? true : false,
                                        min: frequencyOption === 'interval' ? { value: 1, message: "Must be at least 1." } : undefined,
                                        validate: value => {
                                            if (frequencyOption !== 'interval') return true;
                                            if (frequencyOption === 'interval' && !Number.isInteger(value)) {
                                                return "Must be a whole number.";
                                            }
                                            return true;
                                        }
                                    })}
                                    disabled={frequencyOption !== 'interval'}
                                    className={`w-16 ml-2 rounded-xl border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm sm:leading-6 ${errors.frequencyIntervalValue ? 'border-red-500' : 'border-gray-300'} ${frequencyOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                <select
                                    {...register("frequencyIntervalUnit", { required: frequencyOption === 'interval' ? "Unit is required." : false })}
                                    disabled={frequencyOption !== 'interval'}
                                    className={`ml-2 rounded-xl border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm sm:leading-6 ${errors.frequencyIntervalUnit ? 'border-red-500' : 'border-gray-300'} ${frequencyOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    {frequencyIntervalUnits.map(unit => (
                                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="flex items-center ml-6">
                                <input
                                    id="frequencyOptionMonthly"
                                    type="radio"
                                    value="monthly"
                                    {...register("frequencyOption")}
                                    className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                                />
                                <span className="ml-2">Monthly</span>
                            </label>
                        </div>
                        {errors.frequencyIntervalValue && frequencyOption === 'interval' && <p className="mt-1 text-xs text-red-500 ml-7">{errors.frequencyIntervalValue.message}</p>}
                        {errors.frequencyIntervalUnit && frequencyOption === 'interval' && <p className="mt-1 text-xs text-red-500 ml-7">{errors.frequencyIntervalUnit.message}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <InputField label="End Date (Optional)" id="endDate" type="date"
                                    register={register("endDate")}
                                />
                            </div>
                            <div>
                                <InputField label="No. of Occurrences (Optional)" id="occurrences" type="number" min="1"
                                    register={register("occurrences", { min: { value: 1, message: "Must be at least 1." } })}
                                    error={errors.occurrences}
                                    placeholder="e.g., 10"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Allocation Type section (with dynamic required) --- */}
                {/* <div className="pt-8 mt-8 border-t border-gray-200"> */}
                    <div className="space-y-6 mt-6">
                        {/* Allocation Type Pills */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Allocation Type{quoteStatus === 'pending-quote' ? '' : ' *'}
                            </label>
                            <div className="flex space-x-4">
                                {allocationTypes.map(type => (
                                    <label key={type.value} className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            value={type.value}
                                            {...register("allocationType", { required: quoteStatus === 'pending-quote' ? false : "Please select an allocation type." })}
                                            className="sr-only"
                                        />
                                        <div className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${
                                            allocationType === type.value 
                                                ? 'border-green-600 bg-green-600 text-white' 
                                                : 'border-gray-300 bg-white text-gray-700 hover:border-green-600'
                                        }`}>
                                            {type.label}
                                        </div>
                                    </label>
                                ))}                            </div>
                            {errors.allocationType && <p className="mt-2 text-xs text-red-500">{errors.allocationType.message}</p>}
                        </div>

                        {/* NDIS Job Checkbox */}
                        <div>
                            <label className="inline-flex items-center">
                                <input
                                    type="checkbox"
                                    {...register("ndisJob")}
                                    className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">NDIS Job</span>
                            </label>
                        </div>

                        {/* Conditional Fields Based on Allocation Type */}
                        {allocationType && (
                            <div className="space-y-6">
                                {/* For Set Fee: Show FSE Price, Invoice Price (ex. GST), and Invoice Price */}
                                {allocationType === 'fixed-fee' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">                                        <InputField
                                            label={`FSE Price (${ndisJob ? 'GST Free' : 'w/ GST'})${quoteStatus === 'approved' ? ' *' : ''}`}
                                            id="pricePerHour" type="number" step="0.01" min="0"
                                            register={register("pricePerHour", {
                                                required: quoteStatus === 'approved' ? "FSE price per hour is required for approved jobs." : false,
                                                valueAsNumber: true,
                                                min: { value: 0, message: "Cannot be negative." }
                                            })}
                                            error={errors.pricePerHour}
                                            placeholder="e.g., 55.00"
                                        />
                                        <InputField
                                            label="Invoice Price (ex. GST)"
                                            id="additionalPrice" type="number" step="0.01" min="0"
                                            register={register("additionalPrice", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                                            error={errors.additionalPrice}
                                            placeholder="e.g., 77.00"
                                        />
                                        <InputField
                                            label={`Invoice Price (${ndisJob ? 'GST Free' : 'w/ GST'})`}
                                            id="invoicePricePerHour" type="number" step="0.01" min="0"
                                            register={register("invoicePricePerHour", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                                            error={errors.invoicePricePerHour}
                                            placeholder="e.g., 60.50"
                                        />
                                    </div>
                                )}

                                {/* For Fixed Hours: Show allocation frequency options and fields */}
                                {allocationType === 'fixed-hours' && (
                                    <div className="space-y-6">
                                        {/* Allocation Frequency Options - Only show if schedule is recurring */}
                                        {scheduleType === 'recurring' && (
                                            <fieldset className="space-y-4">
                                                <legend className="block text-sm font-medium text-gray-700 mb-0">Allocation Frequency *</legend>
                                                <p className="italic text-sm text-gray-600 mt-1 mb-1">Allocation is important for locking in the hours alloted for the job. The franchisee cannot exceed the allocated hours for the particular frequency selected</p>
                                                <div className="flex items-center gap-8">
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            id="allocationOptionInterval"
                                                            type="radio"
                                                            value="interval"
                                                            {...register("allocationOption", { required: "Please select an allocation frequency option." })}
                                                            className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                                                        />
                                                        <label htmlFor="allocationOptionInterval" className="flex items-center space-x-2 text-sm text-gray-700">
                                                            <span>Every</span>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="1"
                                                                {...register("allocationIntervalValue", {
                                                                    required: allocationOption === 'interval' && "Value is required.",
                                                                    valueAsNumber: true,
                                                                    min: allocationOption === 'interval' ? { value: 1, message: "Must be at least 1." } : undefined,
                                                                    validate: value => allocationOption !== 'interval' || Number.isInteger(value) || "Must be a whole number."
                                                                })}
                                                                disabled={allocationOption !== 'interval'}
                                                                className={`w-16 rounded-xl border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm sm:leading-6 ${errors.allocationIntervalValue ? 'border-red-500' : 'border-gray-300'} ${allocationOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                            />
                                                            <select
                                                                {...register("allocationIntervalUnit", { required: allocationOption === 'interval' && "Unit is required." })}
                                                                disabled={allocationOption !== 'interval'}
                                                                className={`rounded-xl border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm sm:leading-6 ${errors.allocationIntervalUnit ? 'border-red-500' : 'border-gray-300'} ${allocationOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                            >
                                                                {allocationIntervalUnits.map(unit => (
                                                                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            id="allocationOptionMonthly"
                                                            type="radio"
                                                            value="monthly"
                                                            {...register("allocationOption", { required: "Please select an allocation frequency option." })}
                                                            className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                                                        />
                                                        <label htmlFor="allocationOptionMonthly" className="text-sm text-gray-700">Monthly</label>
                                                    </div>
                                                </div>
                                                {errors.allocationIntervalValue && allocationOption === 'interval' && <p className="mt-1 text-xs text-red-500 ml-7">{errors.allocationIntervalValue.message}</p>}
                                                {errors.allocationIntervalUnit && allocationOption === 'interval' && <p className="mt-1 text-xs text-red-500 ml-7">{errors.allocationIntervalUnit.message}</p>}
                                                {errors.allocationOption && <p className="mt-1 text-xs text-red-500">{errors.allocationOption.message}</p>}
                                            </fieldset>
                                        )}

                                        {/* Fixed Hours, Invoice Price (ex. GST), FSE Price, Invoice Price */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                            <InputField label="Fixed Allocated Hours" id="allotedHours" type="number" step="0.1" min="0"
                                                register={register("allotedHours", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                                                error={errors.allotedHours}
                                                placeholder="e.g., 1.5"
                                            />
                                            <InputField
                                                label={`FSE price/hour (${ndisJob ? 'GST Free' : 'w/ GST'})${quoteStatus === 'approved' ? ' *' : ''}`}
                                                id="pricePerHour" type="number" step="0.01" min="0"
                                                register={register("pricePerHour", {
                                                    required: quoteStatus === 'approved' ? "FSE price per hour is required for approved jobs." : false,
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                error={errors.pricePerHour}
                                                placeholder="e.g., 55.00"
                                            />
                                            <InputField
                                                label="Invoice Price (ex. GST)"
                                                id="additionalPrice" type="number" step="0.01" min="0"
                                                register={register("additionalPrice", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                                                error={errors.additionalPrice}
                                                placeholder="e.g., 77.00"
                                            />
                                            <InputField
                                                label={`Invoice Price (${ndisJob ? 'GST Free' : 'w/ GST'})`}
                                                id="invoicePricePerHour" type="number" step="0.01" min="0"
                                                register={register("invoicePricePerHour", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                                                error={errors.invoicePricePerHour}
                                                placeholder="e.g., 60.50"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                {/* </div> */}

                <div className="mt-6">
                    <TextareaField label="Description (Invoice notes)" id="description" rows={3}
                        register={register("description")}
                        error={errors.description}
                        placeholder="Enter notes that will appear on the client invoice..."
                    />
                </div>
                <div className="mt-4">
                    <TextareaField label="Admin notes" id="adminNotes" rows={3}
                        register={register("adminNotes")}
                        error={errors.adminNotes}
                        placeholder="Internal notes for admin use only..."
                    />
                </div>
                <div>
                    <TextareaField label="Notes for franchisee" id="notesForFranchisee" rows={3}
                        register={register("notesForFranchisee")}
                        error={errors.notesForFranchisee}
                        placeholder="Enter any specific instructions or notes for the franchisee..."
                    />
                </div>

                {/* Sticky Action Bar
                  Variant A (swap): full viewport width
                        {/* <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)] flex justify-end gap-3 px-6 py-3">
                          ... buttons...
                           </div/
                  Variant B (active): replace this div with:
                  */}
                  <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                    <div className="container mx-auto px-6 py-3 max-w-4xl flex justify-end gap-3">
                       <Link
                        to="/job-scheduler"
                        state={location.state?.filters ? { filters: location.state.filters } : {}}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </Link>
                    <Button type="submit" variant="primary" disabled={isSubmitting || franchiseeSearch.isSearching || supplierSearch.isSearching || appClientSearch.isSearching}>
                        {isSubmitting ? (quoteStatus === 'pending-quote' ? 'Sending...' : 'Scheduling...') : (quoteStatus === 'pending-quote' ? 'Send Request for Quote' : 'Schedule Job(s)')}
                    </Button>
                    </div>
                  </div>

                {isSubmitted && (Object.keys(errors).length > 0 || serviceSelectionError || apiError) && (
                    <div className="mt-4 text-center text-red-600 text-sm">
                        <FaExclamationTriangle className="inline mr-1" /> Please correct the errors in the form above.
                    </div>
                )}

                <ConfirmationModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={handleConfirm}
                    title={formDataForConfirmation?.quoteStatus === 'pending-quote' ? 'Confirm Quote Request' : 'Confirm Job Scheduling'}
                    message={formDataForConfirmation ? buildJobSummary(formDataForConfirmation) : 'No job details entered.'}
                />

                <style jsx>{`
                    .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 {
                        padding: 0.5rem 0.75rem;
                        border-width: 1px;
                        border-radius: 1rem;
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                        outline: none;
                        transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                    }
                    .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6:focus {
                        border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.3);
                    }
                    .suggestions-list {
                        position: absolute;
                        z-index: 10;
                        width: 100%;
                        background-color: white;
                        border: 1px solid #D1D5DB; border-radius: 1rem;
                        margin-top: 0.25rem;
                        max-height: 15rem;
                        overflow-y: auto;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    }
                    .suggestion-item {
                        padding: 0.5rem 0.75rem;
                        cursor: pointer;
                    }
                    .suggestion-item:hover {
                        background-color: #67E8F9;
                    }
                    .searching, .no-results {
                         padding: 0.5rem 0.75rem;
                         color: #6B7280;
                    }
                    .btn {
                        padding: 0.5rem 1rem;
                        font-weight: 700;
                        border-radius: 0.375rem;
                        transition: background-color 0.3s ease;
                        border: 1px solid transparent;
                    }
                    .btn-primary {
                        background-color: #06B6D4;
                        color: white;
                    }
                    .btn-primary:hover {
                        background-color: #0E7490;
                    }
                    .btn-primary:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    .btn-secondary {
                        background-color: #E5E7EB;
                        color: #1F2937;
                    }
                    .btn-secondary:hover {
                         background-color: #D1D5DB;
                    }
                `}</style>
            </form>
        </div>
    );
};

export default AddJobSchedulePage;
